import { motion } from 'framer-motion'
import { Paperclip } from 'lucide-react'

interface AnswerOption {
    value: number
    label: string
    description?: string
}

interface Question {
    id: string
    maturity_level?: number
    answer_options?: AnswerOption[]
}

interface DotNavigatorProps {
    questions: Question[]
    answers: Record<string, number>
    attachments: Record<string, File | null>
    existingAttachments: Record<string, string>
    activeIndex: number
    onDotClick: (index: number) => void
}

function isYesNo(q: Question): boolean {
    const opts = q.answer_options || []
    if (opts.length !== 2) return false
    const labels = opts.map(o => o.label.toLowerCase())
    return labels.includes('yes') && labels.includes('no')
}

function getDotColor(q: Question, answer: number | undefined): string {
    if (answer === undefined) return 'bg-gray-100 text-gray-500 border border-gray-200'

    if (isYesNo(q)) {
        return answer > 0
            ? 'bg-gray-100 text-gray-600 border-2 border-green-400'
            : 'bg-gray-100 text-gray-600 border-2 border-red-300'
    }

    // MCQ: green outline for any answer
    return 'bg-gray-100 text-gray-600 border-2 border-green-400'
}

export function DotNavigator({
    questions,
    answers,
    attachments,
    existingAttachments,
    activeIndex,
    onDotClick,
}: DotNavigatorProps) {
    // Group questions by maturity level
    const groups: { level: number; items: { q: Question; i: number }[] }[] = []
    const levelMap = new Map<number, { q: Question; i: number }[]>()

    questions.forEach((q, i) => {
        const level = q.maturity_level || 0
        if (!levelMap.has(level)) {
            levelMap.set(level, [])
        }
        levelMap.get(level)!.push({ q, i })
    })

    // Sort by level
    const sortedLevels = Array.from(levelMap.keys()).sort((a, b) => a - b)
    for (const level of sortedLevels) {
        groups.push({ level, items: levelMap.get(level)! })
    }

    return (
        <div className="flex flex-col gap-3 p-2">
            {groups.map((group) => (
                <div key={group.level}>
                    <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">
                        {group.level > 0 ? `Level ${group.level}` : 'General'}
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                        {group.items.map(({ q, i }) => {
                            const answer = answers[q.id]
                            const isCurrent = i === activeIndex
                            const colorClass = getDotColor(q, answer)

                            return (
                                <motion.button
                                    key={q.id}
                                    type="button"
                                    onClick={() => onDotClick(i)}
                                    className={`
                                        w-6 h-6 rounded-full text-[9px] font-semibold
                                        flex items-center justify-center
                                        transition-all duration-200 select-none
                                        relative overflow-visible
                                        ${colorClass}
                                        ${isCurrent ? 'ring-2 ring-blue-500 ring-offset-1 scale-110 shadow-md' : 'hover:scale-105'}
                                    `}
                                    whileHover={{ scale: 1.15 }}
                                    whileTap={{ scale: 0.95 }}
                                    title={`Q${i + 1}${answer !== undefined ? (isYesNo(q) ? (answer > 0 ? ' ✓ Yes' : ' ✗ No') : ' ✓') : ''}${attachments[q.id] || existingAttachments[q.id] ? ' (with attachment)' : ''}`}
                                >
                                    {i + 1}
                                    {(attachments[q.id] || existingAttachments[q.id]) && (
                                        <div className="absolute -top-1 -right-1.5 z-10 p-0.5 bg-blue-500 rounded-full border border-white shadow-sm ring-1 ring-blue-500/20">
                                            <Paperclip className="w-1.5 h-1.5 text-white" />
                                        </div>
                                    )}
                                </motion.button>
                            )
                        })}
                    </div>
                </div>
            ))}
        </div>
    )
}
